import groundcover from '@groundcover/browser';
import { telemetryEnabled } from './telemetry';

/**
 * App logger. In local dev everything logs to the console only.
 * In deployed environments: debug is dropped, info is sent to groundcover
 * only, and warn/error log to the console, which groundcover forwards.
 * Without groundcover configured, everything falls back to the console.
 */

type LogLevel = 'error' | 'warn' | 'info' | 'debug';

const isDeployedEnv = Boolean(process.env.REACT_APP_NODE_ENV)
	&& process.env.REACT_APP_NODE_ENV !== 'local';

function logToConsole(level: LogLevel, args: unknown[]): void {
	// eslint-disable-next-line no-console -- this IS the console wrapper
	(console[level] as (...a: unknown[]) => void)(...args);
}

// Make a value safe to JSON-stringify — Errors stringify to {}, and one
// circular reference would drop groundcover's whole event batch.
function toSerializable(value: unknown): unknown {
	if (value instanceof Error) {
		return { name: value.name, message: value.message, stack: value.stack };
	}
	try {
		JSON.stringify(value);
		return value;
	} catch {
		return String(value);
	}
}

// A leading string arg becomes the message; the rest is queryable context.
function buildAttributes(args: unknown[]): Record<string, unknown> {
	const [first, ...rest] = args;
	const hasMessage = typeof first === 'string';
	const context = (hasMessage ? rest : args).map(toSerializable);

	const attributes: Record<string, unknown> = {
		message: hasMessage ? first : JSON.stringify(context[0] ?? null),
	};
	if (context.length === 1) [attributes.context] = context;
	else if (context.length > 1) attributes.context = context;
	return attributes;
}

function sendLogEvent(level: LogLevel, args: unknown[]): void {
	groundcover.sendCustomEvent({
		event: `log.${level}`,
		attributes: buildAttributes(args),
	});
}

function captureAnyError(args: unknown[]): void {
	const err = args.find((a): a is Error => a instanceof Error);
	if (err) {
		groundcover.captureException(err);
	}
}

function send(level: LogLevel, args: unknown[]): void {
	if (isDeployedEnv && level === 'debug') return;

	// warn/error go to the console instead — groundcover forwards those
	// itself, so a custom event would send them twice
	if (isDeployedEnv && telemetryEnabled && level === 'info') {
		sendLogEvent(level, args);
		return;
	}

	logToConsole(level, args);
	if (telemetryEnabled) captureAnyError(args);
}

const logger = {
	error: (...args: unknown[]) => send('error', args),
	warn: (...args: unknown[]) => send('warn', args),
	info: (...args: unknown[]) => send('info', args),
	debug: (...args: unknown[]) => send('debug', args),
	log: (...args: unknown[]) => send('info', args),
};

export default logger;
